import React, { useEffect, useState } from "react";
import { toast } from "react-hot-toast";

import { fetchUserRoles } from "../services/approvalApi";
import {
  fetchRequestApprovalLevels,
  addRequestApprovalLevel,
  updateRequestApprovalLevel,
  toggleRequestApprovalLevelStatus,
} from "../services/requestApprovalApi";

const REQUEST_TYPES = ["Forecast", "Exceptional", "Outsourced"];

/**
 * Admin screen to configure the hiring-REQUEST approval chain.
 * A level = one approval step: a role, its order, and which request_type it applies to.
 * Deactivating all levels for a request_type => that type needs no approval.
 * Mirrors ApprovalLevelsManager (candidate flow) but adds a role selector.
 */
export default function RequestApprovalLevelsManager() {
  const [levels, setLevels] = useState([]);
  const [roles, setRoles] = useState([]);
  const [editingLevel, setEditingLevel] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [togglingLevel, setTogglingLevel] = useState(null);
  const [formData, setFormData] = useState({ level_name: "", role_name: "", level_order: "", request_type: "" });
  const [filters, setFilters] = useState({ level_name: "", request_type: "", is_active: "" });

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [fetchedLevels, fetchedRoles] = await Promise.all([
        fetchRequestApprovalLevels(),
        fetchUserRoles(),
      ]);
      setLevels(Array.isArray(fetchedLevels) ? fetchedLevels : []);
      setRoles(Array.isArray(fetchedRoles) ? fetchedRoles : []);
    } catch (err) {
      console.error("Error loading data", err);
      toast.error("Failed to load request approval levels or roles.");
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async () => {
    const { level_name, role_name, level_order, request_type } = formData;
    if (!level_name || !role_name || !level_order || !request_type) {
      toast.error("Please fill in all required fields.");
      return;
    }
    try {
      if (editingLevel) {
        await updateRequestApprovalLevel({
          request_approval_level_id: editingLevel.request_approval_level_id,
          level_name,
          role_name,
          level_order: Number(level_order),
          request_type,
        });
        toast.success("Request approval level updated.");
      } else {
        await addRequestApprovalLevel({
          level_name,
          role_name,
          level_order: Number(level_order),
          request_type,
        });
        toast.success("Request approval level added.");
      }
      setFormData({ level_name: "", role_name: "", level_order: "", request_type: "" });
      setEditingLevel(null);
      setShowModal(false);
      loadData();
    } catch (err) {
      console.error("Error submitting form", err);
      toast.error("Failed to save request approval level.");
    }
  };

  const handleEdit = (level) => {
    setEditingLevel(level);
    setFormData({
      level_name: level.level_name || "",
      role_name: level.role_name || "",
      level_order: level.level_order ?? "",
      request_type: level.request_type || "",
    });
    setShowModal(true);
  };

  const handleAddNew = () => {
    setEditingLevel(null);
    setFormData({ level_name: "", role_name: "", level_order: "", request_type: "" });
    setShowModal(true);
  };

  const handleToggleConfirm = async () => {
    try {
      const newStatus = !togglingLevel.is_active;
      await toggleRequestApprovalLevelStatus(togglingLevel.request_approval_level_id, newStatus);
      toast.success(`Level ${newStatus ? "activated" : "deactivated"}.`);
      setShowStatusModal(false);
      setTogglingLevel(null);
      loadData();
    } catch (err) {
      console.error("Error toggling status", err);
      toast.error("Failed to update status.");
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const filteredLevels = levels.filter((level) =>
    (filters.level_name === "" || level.level_name?.toLowerCase().includes(filters.level_name.toLowerCase())) &&
    (filters.request_type === "" || level.request_type === filters.request_type) &&
    (filters.is_active === "" || String(level.is_active) === filters.is_active)
  );

  const inputStyle = { borderColor: "rgba(18, 76, 114, 0.3)", backgroundColor: "white" };

  return (
    <div className="flex flex-col 2xl:px-[10%] pt-8 bg-white md:pt-8">
      <div className="bg-white rounded-2xl p-6 filter drop-shadow-[0_5px_30px_rgba(10,60,96,0.10)] overflow-y-auto relative">
        <div className="flex flex-col md:flex-row md:items-center md:gap-4 w-full">
          <h3 className="text-xl sm:text-2xl text-primary-800 font-semibold mb-4 md:mb-0">
            Request Approval Levels
          </h3>
          <div className="flex flex-wrap md:flex-nowrap gap-4 ml-auto w-full md:w-auto">
            <button
              onClick={handleAddNew}
              className="w-full sm:w-auto bg-primary-800 text-white px-6 py-2 rounded-xl text-base"
            >
              + Add New Level
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-5">
          <div className="space-y-1">
            <label className="block text-xs font-medium" style={{ color: "rgb(18, 76, 114)" }}>Level Name</label>
            <input
              type="text" name="level_name" value={filters.level_name} onChange={handleFilterChange}
              placeholder="Search..." className="w-full px-3 py-2 border rounded-lg outline-none text-sm text-gray-700"
              style={{ borderColor: "rgba(18, 76, 114, 0.3)" }}
            />
          </div>
          <div className="space-y-1">
            <label className="block text-xs font-medium" style={{ color: "rgb(18, 76, 114)" }}>Request Type</label>
            <select
              name="request_type" value={filters.request_type} onChange={handleFilterChange}
              className="w-full px-3 py-2 border rounded-lg outline-none text-sm text-gray-700"
              style={{ borderColor: "rgba(18, 76, 114, 0.3)" }}
            >
              <option value="">All Types</option>
              {REQUEST_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div className="space-y-1">
            <label className="block text-xs font-medium" style={{ color: "rgb(18, 76, 114)" }}>Status</label>
            <select
              name="is_active" value={filters.is_active} onChange={handleFilterChange}
              className="w-full px-3 py-2 border rounded-lg outline-none text-sm text-gray-700"
              style={{ borderColor: "rgba(18, 76, 114, 0.3)" }}
            >
              <option value="">All Status</option>
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </select>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto pt-6">
          <table className="min-w-full table-auto border-collapse">
            <thead className="bg-primary-800/10 text-primary-800 text-left">
              <tr>
                <th className="py-3 px-4">Level Name</th>
                <th className="px-4">Role</th>
                <th className="px-4">Order</th>
                <th className="px-4">Request Type</th>
                <th className="px-4">Status</th>
                <th className="px-4 text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredLevels
                .slice()
                .sort((a, b) => (a.request_type || "").localeCompare(b.request_type || "") || (a.level_order - b.level_order))
                .map((level, index) => (
                  <tr key={level.request_approval_level_id} className={`${index % 2 === 0 ? "bg-white" : "bg-primary-800/10"} border-b text-sm text-primary-600`}>
                    <td className="px-4 py-2 border-r text-[#232323]">{level.level_name}</td>
                    <td className="px-4 py-2 border-r text-[#232323]">{level.role_name}</td>
                    <td className="px-4 py-2 border-r text-[#232323]">{level.level_order}</td>
                    <td className="px-4 py-2 border-r text-[#232323]">{level.request_type}</td>
                    <td className="px-4 py-2 border-r">
                      <span
                        className="px-2.5 py-1 rounded-full text-xs font-medium"
                        style={{
                          backgroundColor: level.is_active ? "rgba(22, 163, 74, 0.1)" : "rgba(220, 38, 38, 0.1)",
                          color: level.is_active ? "rgb(21, 128, 60)" : "rgb(185, 28, 28)",
                        }}
                      >
                        {level.is_active ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex space-x-2 justify-center">
                        <button
                          onClick={() => handleEdit(level)}
                          className="bg-primary-800/10 hover:bg-primary-800 text-primary-800 hover:text-white px-4 py-2 rounded-lg text-sm font-medium border border-primary-800/20"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => { setTogglingLevel(level); setShowStatusModal(true); }}
                          className="px-4 py-2 rounded-lg text-sm font-medium border"
                          style={{
                            backgroundColor: level.is_active ? "rgba(220, 38, 38, 0.05)" : "rgba(22, 163, 74, 0.05)",
                            color: level.is_active ? "rgb(185, 28, 28)" : "rgb(21, 128, 60)",
                            borderColor: level.is_active ? "rgba(220, 38, 38, 0.3)" : "rgba(22, 163, 74, 0.3)",
                          }}
                        >
                          {level.is_active ? "Deactivate" : "Activate"}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              {filteredLevels.length === 0 && (
                <tr>
                  <td colSpan={6} className="text-center text-gray-500 py-4">No request approval levels found.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add / Edit modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-xl w-full max-w-md p-6 shadow-xl">
            <h2 className="text-lg font-semibold mb-4" style={{ color: "rgb(18, 76, 114)" }}>
              {editingLevel ? "Edit" : "Add"} Approval Level
            </h2>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium mb-1" style={{ color: "rgb(18, 76, 114)" }}>Level Name *</label>
                <input
                  type="text" name="level_name" value={formData.level_name} onChange={handleChange}
                  placeholder="e.g. Finance" className="w-full px-3 py-2 border rounded-lg outline-none text-sm" style={inputStyle}
                />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1" style={{ color: "rgb(18, 76, 114)" }}>Approver Role *</label>
                <select name="role_name" value={formData.role_name} onChange={handleChange} className="w-full px-3 py-2 border rounded-lg outline-none text-sm" style={inputStyle}>
                  <option value="">Select role...</option>
                  {roles.map((r) => {
                    const val = r.role || r.role_name || r.roleName || r.name || "";
                    return <option key={r.userRoles_id ?? val} value={val}>{val}</option>;
                  })}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium mb-1" style={{ color: "rgb(18, 76, 114)" }}>Level Order *</label>
                <input
                  type="number" name="level_order" value={formData.level_order} onChange={handleChange} min={1}
                  placeholder="1" className="w-full px-3 py-2 border rounded-lg outline-none text-sm" style={inputStyle}
                />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1" style={{ color: "rgb(18, 76, 114)" }}>Request Type *</label>
                <select name="request_type" value={formData.request_type} onChange={handleChange} className="w-full px-3 py-2 border rounded-lg outline-none text-sm" style={inputStyle}>
                  <option value="">Select type...</option>
                  {REQUEST_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button onClick={() => { setShowModal(false); setEditingLevel(null); }} className="px-4 py-2 rounded-md border text-sm" style={{ color: "rgb(18, 76, 114)", borderColor: "rgba(18,76,114,0.3)" }}>Cancel</button>
              <button onClick={handleSubmit} className="px-4 py-2 rounded-md bg-primary-800 text-white text-sm">Save</button>
            </div>
          </div>
        </div>
      )}

      {/* Toggle-status confirm modal */}
      {showStatusModal && togglingLevel && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-xl w-full max-w-sm p-6 shadow-xl">
            <h2 className="text-lg font-semibold mb-2" style={{ color: "rgb(18, 76, 114)" }}>
              {togglingLevel.is_active ? "Deactivate" : "Activate"} Level
            </h2>
            <p className="text-sm text-gray-500 mb-4">
              {togglingLevel.is_active
                ? `"${togglingLevel.level_name}" will no longer be part of the ${togglingLevel.request_type} approval chain.`
                : `"${togglingLevel.level_name}" will be added back to the ${togglingLevel.request_type} approval chain.`}
            </p>
            <div className="flex justify-end gap-2">
              <button onClick={() => { setShowStatusModal(false); setTogglingLevel(null); }} className="px-4 py-2 rounded-md border text-sm" style={{ color: "rgb(18, 76, 114)", borderColor: "rgba(18,76,114,0.3)" }}>Cancel</button>
              <button onClick={handleToggleConfirm} className="px-4 py-2 rounded-md bg-primary-800 text-white text-sm">Confirm</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
